// 'authentik' strategy — online/cloud mode. Phone + PIN through the business
// application's headless flow (authentik_flow.ts); refresh via the
// refresh_token grant (offline_access). Self-registers on import.

import { BUSINESS_AUTH } from '../env';
import { bizUid, normalizeIraqiPhone } from '../digits';
import { AuthentikFlowClient, refreshTokens, type FlowClaims, type FlowTokens } from './authentik_flow';
import { registerStrategy } from './registry';
import { AuthError, AUTH_COPY, type AuthStrategy, type LoginResult, type SessionUser } from './types';

function userFrom(claims: FlowClaims, phone: string): SessionUser {
  const name = claims.name || claims.preferred_username?.replace(/^biz:/, '') || phone;
  return {
    id: claims.sub,
    name,
    phone: claims.phone_number || phone,
    initial: name.trim().charAt(0) || '؟',
  };
}

/** Flow tokens → the session shape (login and the web onboarding's enrollment both end here). */
export function flowLoginResult(t: FlowTokens, phone: string): LoginResult {
  return {
    tokens: { accessToken: t.accessToken, refreshToken: t.refreshToken, expiresIn: t.expiresIn },
    user: userFrom(t.claims, phone),
  };
}

export const authentikStrategy: AuthStrategy = {
  name: 'authentik',
  async login(phone, pin) {
    const uid = bizUid(phone);
    const e164 = normalizeIraqiPhone(phone);
    if (!uid || !e164) throw new AuthError('invalid_phone', AUTH_COPY.invalidPhone);
    const tokens = await new AuthentikFlowClient(BUSINESS_AUTH.clientId).login(uid, pin);
    return flowLoginResult(tokens, e164);
  },
  async refresh(refreshToken) {
    if (!refreshToken) return null;
    try {
      const t = await refreshTokens(BUSINESS_AUTH.clientId, refreshToken);
      return flowLoginResult(t, '');
    } catch {
      return null;
    }
  },
  async logout() {
    try {
      await new AuthentikFlowClient(BUSINESS_AUTH.clientId).logout();
    } catch {
      /* best effort */
    }
  },
};

registerStrategy('authentik', () => authentikStrategy);
